import { useEffect, useMemo, useRef, useState } from 'react'
import {
  Activity, Server, Loader2, AlertCircle, RefreshCw, Settings2, Check,
  ChevronDown, Play, Info, Search, SlidersHorizontal, History, Clock,
  FileText, Trash2, Download, CalendarClock, ArrowLeft,
} from 'lucide-react'
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts'
import { api } from '../utils/api'

// ── Types ─────────────────────────────────────────────────────────────────────

type Period = 'daily' | 'monthly' | 'yearly'
type AiProvider = 'gemini' | 'claude' | 'openai' | 'groq'

interface ZbxHost { hostid: string; name: string; enabled: boolean; groups: string[] }
interface ZbxItemOpt { itemid: string; hostid: string; name: string; key: string; units: string; valueType: number }

interface Metric {
  itemid: string; key: string; name: string; units: string
  min: number; avg: number; max: number; last: number | null; samples: number
}
interface HostSummary { hostid: string; name: string; metrics: Metric[] }
interface AnalysisSummary {
  period: Period; periodLabel: string; source: string
  from: string; till: string
  hosts: HostSummary[]
  events: { total: number; bySeverity: Record<string, number>; top: { name: string; severity: string; host: string; at: string }[] }
  activeProblems: { total: number; bySeverity: Record<string, number> }
}
interface ReportListItem {
  id: string; period: Period; trigger: string; title: string; hostCount: number
  provider?: string | null; status: string; error?: string | null
  createdAt: string; createdByName?: string | null
}
interface ReportDetail extends ReportListItem { summary: AnalysisSummary | null; narrative: string | null }

const PERIODS: { key: Period; label: string; hint: string }[] = [
  { key: 'daily',   label: 'รายวัน',  hint: '24 ชม.ล่าสุด · ข้อมูลดิบ (history)' },
  { key: 'monthly', label: 'รายเดือน', hint: '30 วันล่าสุด · ค่าเฉลี่ยรายชั่วโมง (trends)' },
  { key: 'yearly',  label: 'รายปี',   hint: '365 วันล่าสุด · ค่าเฉลี่ยรายชั่วโมง (trends)' },
]
const PERIOD_LABEL: Record<Period, string> = { daily: 'รายวัน', monthly: 'รายเดือน', yearly: 'รายปี' }

const PROVIDER_LABEL: Record<AiProvider, string> = {
  gemini: 'Gemini', claude: 'Claude', openai: 'ChatGPT', groq: 'Groq',
}

const SEVERITY_COLOR: Record<string, string> = {
  'วิกฤต': 'bg-red-100 text-red-700 border-red-200',
  'สูง': 'bg-orange-100 text-orange-700 border-orange-200',
  'ปานกลาง': 'bg-amber-100 text-amber-700 border-amber-200',
  'เตือน': 'bg-yellow-100 text-yellow-700 border-yellow-200',
  'ข้อมูล': 'bg-blue-100 text-blue-700 border-blue-200',
  'ไม่จัดระดับ': 'bg-gray-100 text-gray-600 border-gray-200',
}
const CHART_COLORS = ['#2563eb', '#16a34a', '#d97706', '#dc2626', '#7c3aed', '#0891b2']

const fmtDateTime = (s: string) =>
  new Date(s).toLocaleString('th-TH', { dateStyle: 'medium', timeStyle: 'short' })

// ── Tiny markdown renderer ───────────────────────────────────────────────────

function Markdown({ text }: { text: string }) {
  const lines = text.split('\n')
  const out: JSX.Element[] = []
  let list: string[] = []
  const inline = (s: string) =>
    s.split(/(`[^`]+`|\*\*[^*]+\*\*)/g).map((p, i) => {
      if (p.startsWith('**') && p.endsWith('**')) return <strong key={i}>{p.slice(2, -2)}</strong>
      if (p.startsWith('`') && p.endsWith('`')) return <code key={i} className="bg-gray-100 rounded px-1 font-mono text-[0.9em]">{p.slice(1, -1)}</code>
      return <span key={i}>{p}</span>
    })
  const flush = () => {
    if (!list.length) return
    out.push(<ul key={`ul-${out.length}`} className="list-disc pl-5 space-y-1 my-2">{list.map((li, i) => <li key={i}>{inline(li)}</li>)}</ul>)
    list = []
  }
  lines.forEach((raw, idx) => {
    const line = raw.replace(/\s+$/, '')
    if (/^#{1,6}\s/.test(line)) {
      flush()
      const level = line.match(/^#+/)![0].length
      const content = line.replace(/^#+\s/, '')
      out.push(level <= 2
        ? <h3 key={idx} className="text-base font-bold text-gray-900 mt-4 mb-1.5">{inline(content)}</h3>
        : <h4 key={idx} className="text-sm font-semibold text-gray-800 mt-3 mb-1">{inline(content)}</h4>)
    } else if (/^\s*[-*]\s+/.test(line)) list.push(line.replace(/^\s*[-*]\s+/, ''))
    else if (/^>\s?/.test(line)) {
      flush()
      out.push(<blockquote key={idx} className="border-l-4 border-amber-300 bg-amber-50 text-amber-800 px-3 py-2 my-2 rounded-r-lg text-sm">{inline(line.replace(/^>\s?/, ''))}</blockquote>)
    } else if (line.trim() === '') flush()
    else { flush(); out.push(<p key={idx} className="my-1.5 leading-relaxed">{inline(line)}</p>) }
  })
  flush()
  return <div className="text-sm text-gray-700">{out}</div>
}

function fmt(n: number | null, units: string) {
  if (n == null) return '—'
  const v = Math.abs(n) >= 1000 ? n.toLocaleString(undefined, { maximumFractionDigits: 0 }) : String(n)
  return units ? `${v} ${units}` : v
}

function Stat({ label, value, sub, danger }: { label: string; value: string; sub?: string; danger?: boolean }) {
  return (
    <div className={`rounded-xl border px-3.5 py-2.5 ${danger ? 'bg-red-50 border-red-200' : 'bg-white border-gray-200'}`}>
      <div className="text-[11px] text-gray-400">{label}</div>
      <div className={`text-xl font-bold ${danger ? 'text-red-600' : 'text-gray-900'}`}>{value}</div>
      {sub && <div className="text-[10px] text-gray-400">{sub}</div>}
    </div>
  )
}

// ── Report view (shared by live analysis + history detail) ────────────────────

function ReportView({ summary, narrative, provider, streaming }: {
  summary: AnalysisSummary | null; narrative: string; provider?: string | null; streaming?: boolean
}) {
  const pctChart = useMemo(() => {
    if (!summary) return null
    const names = new Set<string>()
    const rows = summary.hosts.map(h => {
      const row: Record<string, any> = { host: h.name }
      h.metrics.filter(m => m.units === '%').forEach(m => {
        const label = m.name.length > 22 ? m.name.slice(0, 22) + '…' : m.name
        row[label] = m.avg; names.add(label)
      })
      return row
    }).filter(r => Object.keys(r).length > 1)
    return rows.length ? { rows, names: [...names].slice(0, 6) } : null
  }, [summary])

  return (
    <div className="space-y-5">
      {summary && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Stat label="ช่วงเวลา" value={summary.periodLabel.split(' ')[0]} sub={summary.source === 'trends' ? 'trends' : 'history'} />
            <Stat label="Host" value={String(summary.hosts.length)} sub="เครื่อง" />
            <Stat label="เหตุการณ์ในช่วง" value={String(summary.events.total)} sub="events" />
            <Stat label="ปัญหาค้างอยู่" value={String(summary.activeProblems.total)} sub="active" danger={summary.activeProblems.total > 0} />
          </div>

          {Object.keys(summary.events.bySeverity).length > 0 && (
            <div className="flex flex-wrap gap-2">
              {Object.entries(summary.events.bySeverity).map(([sev, n]) => (
                <span key={sev} className={`text-xs px-2.5 py-1 rounded-full border ${SEVERITY_COLOR[sev] ?? 'bg-gray-100 text-gray-600 border-gray-200'}`}>{sev}: {n}</span>
              ))}
            </div>
          )}

          {pctChart && (
            <div className="bg-white border border-gray-200 rounded-2xl p-4">
              <h3 className="text-sm font-semibold text-gray-800 mb-3">การใช้งานทรัพยากรเฉลี่ย (%)</h3>
              <ResponsiveContainer width="100%" height={Math.max(220, pctChart.rows.length * 46)}>
                <BarChart data={pctChart.rows} layout="vertical" margin={{ left: 20, right: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                  <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 11 }} />
                  <YAxis type="category" dataKey="host" width={120} tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  {pctChart.names.map((n, i) => (
                    <Bar key={n} dataKey={n} fill={CHART_COLORS[i % CHART_COLORS.length]} radius={[0, 3, 3, 0]} />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {summary.hosts.map(h => (
            <div key={h.hostid} className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
              <div className="px-4 py-2.5 bg-gray-50 border-b border-gray-100 flex items-center gap-2">
                <Server size={14} className="text-gray-500" />
                <span className="text-sm font-semibold text-gray-800">{h.name}</span>
                <span className="text-xs text-gray-400">{h.metrics.length} metrics</span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-xs text-gray-400 border-b border-gray-100">
                      <th className="text-left font-medium px-4 py-2">Metric</th>
                      <th className="text-right font-medium px-3 py-2">Min</th>
                      <th className="text-right font-medium px-3 py-2">Avg</th>
                      <th className="text-right font-medium px-3 py-2">Max</th>
                      <th className="text-right font-medium px-3 py-2">Last</th>
                      <th className="text-right font-medium px-4 py-2">Samples</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {h.metrics.map(m => (
                      <tr key={m.itemid} className={m.samples === 0 ? 'opacity-40' : ''}>
                        <td className="px-4 py-2">
                          <div className="font-medium text-gray-800">{m.name}</div>
                          <div className="text-[10px] text-gray-400 font-mono">{m.key}</div>
                        </td>
                        <td className="text-right px-3 py-2 text-gray-600 tabular-nums">{fmt(m.min, m.units)}</td>
                        <td className="text-right px-3 py-2 font-semibold text-gray-900 tabular-nums">{fmt(m.avg, m.units)}</td>
                        <td className="text-right px-3 py-2 text-gray-600 tabular-nums">{fmt(m.max, m.units)}</td>
                        <td className="text-right px-3 py-2 text-gray-600 tabular-nums">{fmt(m.last, m.units)}</td>
                        <td className="text-right px-4 py-2 text-gray-400 tabular-nums">{m.samples.toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}

          {summary.events.top.length > 0 && (
            <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
              <div className="px-4 py-2.5 bg-gray-50 border-b border-gray-100 text-sm font-semibold text-gray-800">
                เหตุการณ์ล่าสุด ({summary.events.top.length})
              </div>
              <ul className="divide-y divide-gray-50">
                {summary.events.top.map((e, i) => (
                  <li key={i} className="px-4 py-2 flex items-center gap-2 text-sm">
                    <span className={`text-[10px] px-1.5 py-0.5 rounded border flex-shrink-0 ${SEVERITY_COLOR[e.severity] ?? 'bg-gray-100 text-gray-600 border-gray-200'}`}>{e.severity}</span>
                    <span className="text-gray-800 truncate flex-1">{e.name}</span>
                    <span className="text-xs text-gray-400 flex-shrink-0">{e.host}</span>
                    <span className="text-xs text-gray-300 flex-shrink-0 hidden sm:inline">{fmtDateTime(e.at)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}

      {(narrative || streaming) && (
        <div className="bg-white border border-gray-200 rounded-2xl p-5">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-6 h-6 rounded-lg bg-gradient-to-br from-violet-500 to-blue-600 flex items-center justify-center">
              <Activity size={13} className="text-white" />
            </div>
            <h2 className="text-sm font-semibold text-gray-900">บทวิเคราะห์จาก AI</h2>
            {provider && <span className="text-[10px] text-gray-400">· {provider}</span>}
          </div>
          {narrative
            ? <Markdown text={narrative} />
            : <div className="flex items-center gap-2 text-sm text-gray-400 py-3"><Loader2 size={14} className="animate-spin" /> กำลังประมวลผล…</div>}
        </div>
      )}
    </div>
  )
}

// ── Download helpers ─────────────────────────────────────────────────────────

async function downloadWord(id: string) {
  const res = await api.get(`/zabbix/reports/${id}/export.docx`, { responseType: 'blob' })
  const url = URL.createObjectURL(res.data)
  const a = document.createElement('a')
  a.href = url
  a.download = `zabbix-report-${id}.docx`
  document.body.appendChild(a); a.click(); a.remove()
  setTimeout(() => URL.revokeObjectURL(url), 4000)
}
function openPdf(id: string) {
  window.open(`/zabbix/reports/${id}/print`, '_blank', 'noopener')
}

function DownloadButtons({ id }: { id: string }) {
  const [busy, setBusy] = useState(false)
  return (
    <div className="flex items-center gap-2">
      <button
        onClick={async () => { setBusy(true); try { await downloadWord(id) } catch { alert('ดาวน์โหลดไม่สำเร็จ') } finally { setBusy(false) } }}
        disabled={busy}
        className="flex items-center gap-1.5 text-xs border border-gray-300 hover:bg-gray-50 text-gray-700 px-3 py-1.5 rounded-lg">
        {busy ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />} Word
      </button>
      <button onClick={() => openPdf(id)}
        className="flex items-center gap-1.5 text-xs border border-gray-300 hover:bg-gray-50 text-gray-700 px-3 py-1.5 rounded-lg">
        <FileText size={13} /> PDF
      </button>
    </div>
  )
}

// ── Host multi-select (shared) ───────────────────────────────────────────────

function HostMultiSelect({ hosts, selected, onChange, max = 8 }: {
  hosts: ZbxHost[]; selected: string[]; onChange: (ids: string[]) => void; max?: number
}) {
  const [q, setQ] = useState('')
  const set = new Set(selected)
  const list = q.trim()
    ? hosts.filter(h => h.name.toLowerCase().includes(q.toLowerCase()) || h.groups.some(g => g.toLowerCase().includes(q.toLowerCase())))
    : hosts
  const toggle = (id: string) => {
    const n = new Set(set)
    n.has(id) ? n.delete(id) : n.add(id)
    onChange([...n])
  }
  return (
    <div>
      <div className="relative mb-2">
        <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="ค้นหา host หรือ group…"
          className="w-full border border-gray-200 rounded-lg pl-8 pr-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
      </div>
      <div className="max-h-52 overflow-y-auto border border-gray-100 rounded-xl divide-y divide-gray-50">
        {list.length === 0 && <p className="text-sm text-gray-400 px-3 py-4 text-center">ไม่พบ Host</p>}
        {list.map(h => {
          const on = set.has(h.hostid)
          const disabled = !on && set.size >= max
          return (
            <label key={h.hostid}
              className={`flex items-center gap-2.5 px-3 py-2 text-sm cursor-pointer ${disabled ? 'opacity-40 cursor-not-allowed' : 'hover:bg-gray-50'}`}>
              <input type="checkbox" checked={on} disabled={disabled} onChange={() => toggle(h.hostid)}
                className="w-4 h-4 rounded accent-emerald-600" />
              <Server size={13} className="text-gray-400 flex-shrink-0" />
              <span className="font-medium text-gray-800 truncate">{h.name}</span>
              {!h.enabled && <span className="text-[10px] bg-gray-100 text-gray-400 px-1.5 rounded">ปิดใช้งาน</span>}
              {h.groups[0] && <span className="ml-auto text-[10px] text-gray-400 truncate max-w-[40%]">{h.groups[0]}</span>}
            </label>
          )
        })}
      </div>
    </div>
  )
}

// ── Config panel (admin only) ────────────────────────────────────────────────

function ConfigPanel() {
  const [open, setOpen] = useState(false)
  const [url, setUrl] = useState('')
  const [token, setToken] = useState('')
  const [hasToken, setHasToken] = useState(false)
  const [saving, setSaving] = useState(false)
  const [testing, setTesting] = useState(false)
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null)

  useEffect(() => {
    api.get('/zabbix/config')
      .then(r => { setUrl(r.data.url || ''); setHasToken(!!r.data.hasToken); if (r.data.hasToken) setToken('••••••••') })
      .catch(() => {})
  }, [])

  const save = async () => {
    setSaving(true); setMsg(null)
    try {
      await api.put('/zabbix/config', { url, token })
      setMsg({ ok: true, text: 'บันทึกเรียบร้อย' }); setHasToken(!!token)
    } catch (e: any) {
      setMsg({ ok: false, text: e.response?.data?.message || 'บันทึกไม่สำเร็จ' })
    } finally { setSaving(false) }
  }
  const test = async () => {
    setTesting(true); setMsg(null)
    try {
      const r = await api.post('/zabbix/config/test', { url, token })
      setMsg({ ok: true, text: r.data.message + (r.data.hostCount != null ? ` (${r.data.hostCount} hosts)` : '') })
    } catch (e: any) {
      setMsg({ ok: false, text: e.response?.data?.message || 'เชื่อมต่อไม่สำเร็จ' })
    } finally { setTesting(false) }
  }

  return (
    <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
      <button onClick={() => setOpen(v => !v)} className="w-full flex items-center gap-2 px-4 py-3 text-left hover:bg-gray-50">
        <Settings2 size={15} className="text-gray-500" />
        <span className="text-sm font-medium text-gray-800">การเชื่อมต่อ Zabbix Server</span>
        <span className={`ml-2 text-xs px-2 py-0.5 rounded-full ${hasToken && url ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
          {hasToken && url ? 'ตั้งค่าแล้ว' : 'ยังไม่ตั้งค่า'}
        </span>
        <ChevronDown size={15} className={`ml-auto text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="px-4 pb-4 pt-1 space-y-3 border-t border-gray-100">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Zabbix URL</label>
            <input value={url} onChange={e => setUrl(e.target.value)} placeholder="http://10.0.1.100/zabbix"
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            <p className="text-[11px] text-gray-400 mt-1">ระบบจะเรียก <code>{'{URL}'}/api_jsonrpc.php</code> อัตโนมัติ</p>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">API Token</label>
            <input value={token} onChange={e => setToken(e.target.value)} type="password"
              onFocus={e => { if (e.target.value === '••••••••') setToken('') }}
              placeholder="Zabbix → Users → API tokens"
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
          {msg && <div className={`text-xs rounded-lg px-3 py-2 ${msg.ok ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>{msg.text}</div>}
          <div className="flex gap-2">
            <button onClick={save} disabled={saving || !url}
              className="flex items-center gap-1.5 text-sm bg-blue-600 hover:bg-blue-700 disabled:bg-gray-200 text-white px-3 py-2 rounded-lg">
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} บันทึก
            </button>
            <button onClick={test} disabled={testing || !url}
              className="flex items-center gap-1.5 text-sm border border-gray-300 hover:bg-gray-50 text-gray-700 px-3 py-2 rounded-lg">
              {testing ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} ทดสอบการเชื่อมต่อ
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

// ── Analyze tab ──────────────────────────────────────────────────────────────

function AnalyzeTab({ isAdmin, hosts, hostsErr, hostsLoading, reloadHosts, readyProviders, activeProvider, onSaved }: {
  isAdmin: boolean; hosts: ZbxHost[]; hostsErr: string | null; hostsLoading: boolean
  reloadHosts: () => void; readyProviders: AiProvider[]; activeProvider: AiProvider | null
  onSaved: () => void
}) {
  const [period, setPeriod] = useState<Period>('daily')
  const [selected, setSelected] = useState<string[]>([])
  const [allHosts, setAllHosts] = useState(false)
  const [provider, setProvider] = useState<AiProvider | null>(activeProvider)
  const [providerMenu, setProviderMenu] = useState(false)
  useEffect(() => { setProvider(activeProvider) }, [activeProvider])

  const [pickerOpen, setPickerOpen] = useState(false)
  const [metricsPerHost, setMetricsPerHost] = useState(12)
  const [items, setItems] = useState<ZbxItemOpt[]>([])
  const [itemsLoading, setItemsLoading] = useState(false)
  const [pickedItems, setPickedItems] = useState<Set<string>>(new Set())
  const [itemSearch, setItemSearch] = useState('')

  const [running, setRunning] = useState(false)
  const [progress, setProgress] = useState<string[]>([])
  const [summary, setSummary] = useState<AnalysisSummary | null>(null)
  const [narrative, setNarrative] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [reportId, setReportId] = useState<string | null>(null)
  const abortRef = useRef<AbortController | null>(null)

  useEffect(() => {
    if (!pickerOpen || selected.length === 0) { setItems([]); return }
    setItemsLoading(true)
    api.get('/zabbix/items', { params: { hostIds: selected.join(',') } })
      .then(r => setItems(r.data)).catch(() => setItems([])).finally(() => setItemsLoading(false))
  }, [pickerOpen, selected])

  useEffect(() => {
    setPickedItems(prev => {
      if (prev.size === 0) return prev
      const valid = new Set(items.filter(i => selected.includes(i.hostid)).map(i => i.itemid))
      const next = new Set([...prev].filter(id => valid.has(id)))
      return next.size === prev.size ? prev : next
    })
  }, [items, selected])

  const itemsByHost = useMemo(() => {
    const q = itemSearch.trim().toLowerCase()
    const map = new Map<string, ZbxItemOpt[]>()
    for (const it of items) {
      if (q && !it.name.toLowerCase().includes(q) && !it.key.toLowerCase().includes(q)) continue
      ;(map.get(it.hostid) ?? map.set(it.hostid, []).get(it.hostid)!).push(it)
    }
    return map
  }, [items, itemSearch])

  const run = async () => {
    if ((!selected.length && !allHosts) || running) return
    setRunning(true); setError(null); setProgress([]); setSummary(null); setNarrative(''); setReportId(null)
    const abort = new AbortController()
    abortRef.current = abort
    try {
      const token = localStorage.getItem('token') ?? ''
      const activeCompanyId = localStorage.getItem('activeCompanyId')
      const headers: Record<string, string> = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }
      if (activeCompanyId) headers['X-Company-Id'] = activeCompanyId
      const res = await fetch(`${import.meta.env.VITE_API_URL || '/api'}/zabbix/analyze`, {
        method: 'POST', headers, signal: abort.signal,
        body: JSON.stringify({
          period,
          allHosts: allHosts || undefined,
          hostIds: allHosts ? undefined : selected,
          itemIds: (!allHosts && pickedItems.size) ? [...pickedItems] : undefined,
          maxItemsPerHost: metricsPerHost,
          provider: provider ?? undefined,
        }),
      })
      if (!res.ok) {
        const err = await res.json().catch(() => ({ message: `HTTP ${res.status}` }))
        throw new Error(err.message ?? `HTTP ${res.status}`)
      }
      const reader = res.body!.getReader()
      const decoder = new TextDecoder()
      let buffer = ''
      while (true) {
        const { done, value } = await reader.read()
        if (done) break
        buffer += decoder.decode(value, { stream: true })
        const parts = buffer.split('\n')
        buffer = parts.pop() ?? ''
        for (const line of parts) {
          if (!line.startsWith('data: ')) continue
          let ev: any
          try { ev = JSON.parse(line.slice(6)) } catch { continue }
          if (ev.type === 'progress') setProgress(p => [...p, ev.message])
          else if (ev.type === 'summary') setSummary(ev.summary)
          else if (ev.type === 'text') setNarrative(t => t + ev.text)
          else if (ev.type === 'error') setError(ev.message)
          else if (ev.type === 'done') { setReportId(ev.reportId ?? null); onSaved() }
        }
      }
    } catch (e: any) {
      if (e.name !== 'AbortError') setError(e.message)
    } finally {
      setRunning(false)
    }
  }

  return (
    <div className="space-y-5">
      <div className="bg-white border border-gray-200 rounded-2xl p-4 space-y-4">
        <div>
          <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">ช่วงเวลา</label>
          <div className="flex flex-wrap gap-2">
            {PERIODS.map(p => (
              <button key={p.key} onClick={() => setPeriod(p.key)}
                className={`px-3.5 py-2 rounded-xl text-sm border transition-all ${
                  period === p.key ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'
                }`}>
                <span className="font-medium">{p.label}</span>
                <span className={`block text-[10px] ${period === p.key ? 'text-white/60' : 'text-gray-400'}`}>{p.hint}</span>
              </button>
            ))}
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
              เลือก Host {!allHosts && <span className="text-gray-400 normal-case">(สูงสุด 8)</span>}
            </label>
            <div className="flex items-center gap-2">
              {!allHosts && selected.length > 0 && <span className="text-xs text-gray-400">เลือกแล้ว {selected.length}</span>}
              <button onClick={reloadHosts} className="text-gray-400 hover:text-gray-600"><RefreshCw size={13} /></button>
            </div>
          </div>

          <label className="flex items-center gap-2 mb-2 text-sm cursor-pointer">
            <input type="checkbox" checked={allHosts} onChange={e => setAllHosts(e.target.checked)}
              className="w-4 h-4 rounded accent-emerald-600" />
            <span className="text-gray-700">ดึงทุก Host ที่เปิดใช้งาน{hosts.length ? ` (${hosts.filter(h => h.enabled).length})` : ''}</span>
          </label>

          {hostsLoading ? (
            <div className="flex justify-center py-6"><Loader2 size={18} className="animate-spin text-gray-300" /></div>
          ) : hostsErr ? (
            <div className="text-sm bg-red-50 border border-red-200 text-red-700 rounded-xl px-3 py-2.5 flex items-start gap-2">
              <AlertCircle size={14} className="flex-shrink-0 mt-0.5" />
              <div>{hostsErr}{isAdmin && <div className="text-xs text-red-500 mt-1">ตรวจสอบการตั้งค่าการเชื่อมต่อ Zabbix ด้านบน</div>}</div>
            </div>
          ) : allHosts ? (
            <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2.5">
              จะดึงทุก host ที่ enabled (สูงสุด 250) และเลือก metric อัตโนมัติแบบสมดุล — ช่วง <strong>รายเดือน/รายปี</strong> ที่มี host เยอะ อาจใช้เวลาหลายนาที
            </div>
          ) : (
            <HostMultiSelect hosts={hosts} selected={selected} onChange={setSelected} />
          )}
        </div>

        {/* Advanced metric picker */}
        <div className={`border border-gray-100 rounded-xl overflow-hidden ${allHosts ? 'hidden' : ''}`}>
          <button onClick={() => setPickerOpen(v => !v)} className="w-full flex items-center gap-2 px-3 py-2.5 text-left hover:bg-gray-50">
            <SlidersHorizontal size={14} className="text-gray-500" />
            <span className="text-sm font-medium text-gray-700">เลือก Metric ที่จะวิเคราะห์ (ขั้นสูง)</span>
            {pickedItems.size > 0
              ? <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full">เลือกเอง {pickedItems.size}</span>
              : <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">อัตโนมัติ</span>}
            <ChevronDown size={14} className={`ml-auto text-gray-400 transition-transform ${pickerOpen ? 'rotate-180' : ''}`} />
          </button>
          {pickerOpen && (
            <div className="px-3 pb-3 pt-1 border-t border-gray-100 space-y-3">
              <p className="text-xs text-gray-500">ถ้าไม่เลือกด้านล่าง ระบบจะเลือกให้อัตโนมัติแบบสมดุลทุกหมวด (CPU / RAM / Disk / Network / Availability)</p>
              <div className="flex items-center gap-2">
                <label className="text-xs text-gray-600">จำนวน metric ต่อ host (โหมดอัตโนมัติ)</label>
                <input type="number" min={1} max={30} value={metricsPerHost}
                  onChange={e => setMetricsPerHost(Math.min(30, Math.max(1, Number(e.target.value) || 1)))}
                  className="w-16 border border-gray-200 rounded-lg px-2 py-1 text-sm text-center focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              {selected.length === 0 ? (
                <p className="text-sm text-gray-400 py-2">เลือก Host ก่อน จึงจะเลือก metric ได้</p>
              ) : itemsLoading ? (
                <div className="flex justify-center py-4"><Loader2 size={16} className="animate-spin text-gray-300" /></div>
              ) : (
                <>
                  <div className="flex items-center gap-2">
                    <div className="relative flex-1">
                      <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                      <input value={itemSearch} onChange={e => setItemSearch(e.target.value)} placeholder="ค้นหา metric เช่น memory, vfs.fs, net.if…"
                        className="w-full border border-gray-200 rounded-lg pl-8 pr-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                    </div>
                    {pickedItems.size > 0 && (
                      <button onClick={() => setPickedItems(new Set())} className="text-xs text-gray-400 hover:text-red-500 whitespace-nowrap">ล้างที่เลือก</button>
                    )}
                  </div>
                  <div className="max-h-64 overflow-y-auto border border-gray-100 rounded-xl divide-y divide-gray-50">
                    {selected.map(hid => {
                      const host = hosts.find(h => h.hostid === hid)
                      const list = itemsByHost.get(hid) ?? []
                      return (
                        <div key={hid}>
                          <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-50 sticky top-0">
                            <Server size={12} className="text-gray-400" />
                            <span className="text-xs font-semibold text-gray-600">{host?.name ?? hid}</span>
                            <span className="text-[10px] text-gray-400">{list.length} items</span>
                            {list.length > 0 && (
                              <button
                                onClick={() => setPickedItems(prev => {
                                  const n = new Set(prev)
                                  const allOn = list.every(i => n.has(i.itemid))
                                  list.forEach(i => allOn ? n.delete(i.itemid) : n.add(i.itemid))
                                  return n
                                })}
                                className="ml-auto text-[10px] text-emerald-600 hover:underline">
                                {list.every(i => pickedItems.has(i.itemid)) ? 'ล้างทั้งหมด' : 'เลือกทั้งหมด'}
                              </button>
                            )}
                          </div>
                          {list.length === 0 && <p className="text-xs text-gray-300 px-3 py-2">ไม่พบ metric</p>}
                          {list.map(it => (
                            <label key={it.itemid} className="flex items-center gap-2.5 px-3 py-1.5 text-sm cursor-pointer hover:bg-gray-50">
                              <input type="checkbox" checked={pickedItems.has(it.itemid)}
                                onChange={() => setPickedItems(prev => { const n = new Set(prev); n.has(it.itemid) ? n.delete(it.itemid) : n.add(it.itemid); return n })}
                                className="w-3.5 h-3.5 rounded accent-emerald-600" />
                              <span className="text-gray-800 truncate">{it.name}</span>
                              {it.units && <span className="text-[10px] text-gray-400">{it.units}</span>}
                              <span className="ml-auto text-[10px] text-gray-400 font-mono truncate max-w-[45%]">{it.key}</span>
                            </label>
                          ))}
                        </div>
                      )
                    })}
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center gap-2 pt-1">
          <button onClick={run} disabled={(!selected.length && !allHosts) || running}
            className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-200 disabled:text-gray-400 text-white text-sm font-medium px-4 py-2.5 rounded-xl transition-colors">
            {running ? <Loader2 size={15} className="animate-spin" /> : <Play size={15} />}
            {running ? 'กำลังวิเคราะห์…' : 'วิเคราะห์ด้วย AI'}
          </button>
          {readyProviders.length > 1 && (
            <div className="relative">
              <button onClick={() => setProviderMenu(v => !v)}
                className="flex items-center gap-1.5 text-xs font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-full pl-3 pr-2 py-2">
                {provider ? PROVIDER_LABEL[provider] : 'AI'} <ChevronDown size={12} />
              </button>
              {providerMenu && (
                <div className="absolute left-0 bottom-full mb-2 w-40 bg-white rounded-xl shadow-lg border border-gray-200 py-1 z-10">
                  {readyProviders.map(p => (
                    <button key={p} onClick={() => { setProvider(p); setProviderMenu(false) }}
                      className="w-full flex items-center justify-between px-3 py-2 text-sm text-left hover:bg-gray-50">
                      {PROVIDER_LABEL[p]} {provider === p && <Check size={13} className="text-emerald-600" />}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          {reportId && !running && <DownloadButtons id={reportId} />}
        </div>

        {running && progress.length > 0 && (
          <div className="text-xs text-gray-500 space-y-1 bg-gray-50 rounded-xl px-3 py-2">
            {progress.map((p, i) => (
              <div key={i} className="flex items-center gap-1.5">
                {i === progress.length - 1 && running
                  ? <Loader2 size={11} className="animate-spin text-emerald-500" />
                  : <Check size={11} className="text-emerald-500" />}
                {p}
              </div>
            ))}
          </div>
        )}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm flex items-start gap-2">
          <AlertCircle size={15} className="flex-shrink-0 mt-0.5" /> {error}
        </div>
      )}

      {(summary || narrative || running) && (
        <ReportView summary={summary} narrative={narrative} provider={provider ? PROVIDER_LABEL[provider] : undefined}
          streaming={running && !!summary && !narrative} />
      )}
    </div>
  )
}

// ── History tab ──────────────────────────────────────────────────────────────

function HistoryTab({ isAdmin, refreshKey }: { isAdmin: boolean; refreshKey: number }) {
  const [list, setList] = useState<ReportListItem[]>([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState<Period | 'all'>('all')
  const [detail, setDetail] = useState<ReportDetail | null>(null)
  const [detailLoading, setDetailLoading] = useState(false)

  const load = () => {
    setLoading(true)
    api.get('/zabbix/reports', { params: filter === 'all' ? {} : { period: filter } })
      .then(r => setList(r.data)).catch(() => setList([])).finally(() => setLoading(false))
  }
  useEffect(load, [filter, refreshKey])

  const open = (id: string) => {
    setDetailLoading(true)
    api.get(`/zabbix/reports/${id}`).then(r => setDetail(r.data)).catch(() => {}).finally(() => setDetailLoading(false))
  }
  const remove = async (id: string) => {
    if (!confirm('ลบรายงานนี้?')) return
    try { await api.delete(`/zabbix/reports/${id}`); setList(l => l.filter(r => r.id !== id)); if (detail?.id === id) setDetail(null) }
    catch (e: any) { alert(e.response?.data?.message || 'ลบไม่สำเร็จ') }
  }

  if (detail) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-3 flex-wrap">
          <button onClick={() => setDetail(null)} className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800">
            <ArrowLeft size={15} /> กลับไปรายการ
          </button>
          <span className="text-sm font-semibold text-gray-900">{detail.title}</span>
          <span className="ml-auto"><DownloadButtons id={detail.id} /></span>
        </div>
        <div className="text-xs text-gray-400">
          {fmtDateTime(detail.createdAt)} · {detail.trigger === 'scheduled' ? 'ตัวจับเวลาอัตโนมัติ' : (detail.createdByName || 'ผู้ใช้')}
          {detail.provider && <> · AI: {detail.provider}</>}
        </div>
        {detail.status === 'FAILED'
          ? <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm">ล้มเหลว: {detail.error}</div>
          : <ReportView summary={detail.summary} narrative={detail.narrative || ''} provider={detail.provider || undefined} />}
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        {(['all', 'daily', 'monthly', 'yearly'] as const).map(f => (
          <button key={f} onClick={() => setFilter(f)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium border ${filter === f ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-500 border-gray-200 hover:border-gray-300'}`}>
            {f === 'all' ? 'ทั้งหมด' : PERIOD_LABEL[f]}
          </button>
        ))}
        <button onClick={load} className="ml-auto text-gray-400 hover:text-gray-600"><RefreshCw size={14} /></button>
      </div>

      {loading ? (
        <div className="flex justify-center py-10"><Loader2 size={20} className="animate-spin text-gray-300" /></div>
      ) : list.length === 0 ? (
        <div className="text-center text-sm text-gray-400 py-10 border border-dashed border-gray-200 rounded-2xl">
          ยังไม่มีประวัติการวิเคราะห์
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden divide-y divide-gray-50">
          {detailLoading && <div className="px-4 py-2 text-xs text-gray-400 flex items-center gap-2"><Loader2 size={12} className="animate-spin" /> กำลังเปิด…</div>}
          {list.map(r => (
            <div key={r.id} className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50">
              <button onClick={() => open(r.id)} className="flex-1 min-w-0 text-left">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-800 truncate">{r.title}</span>
                  {r.status === 'FAILED' && <span className="text-[10px] bg-red-100 text-red-600 px-1.5 py-0.5 rounded-full flex-shrink-0">ล้มเหลว</span>}
                  {r.trigger === 'scheduled' && <span className="text-[10px] bg-violet-100 text-violet-600 px-1.5 py-0.5 rounded-full flex-shrink-0">อัตโนมัติ</span>}
                </div>
                <div className="text-xs text-gray-400 mt-0.5">
                  {PERIOD_LABEL[r.period]} · {r.hostCount} host · {fmtDateTime(r.createdAt)}
                  {r.provider && <> · {r.provider}</>}
                </div>
              </button>
              <button onClick={() => open(r.id)} className="text-xs text-emerald-600 hover:underline flex-shrink-0">เปิด</button>
              {r.status !== 'FAILED' && (
                <button onClick={() => downloadWord(r.id).catch(() => alert('ดาวน์โหลดไม่สำเร็จ'))}
                  title="ดาวน์โหลด Word" className="text-gray-400 hover:text-gray-700 flex-shrink-0"><Download size={14} /></button>
              )}
              {r.status !== 'FAILED' && (
                <button onClick={() => openPdf(r.id)} title="เปิด PDF" className="text-gray-400 hover:text-gray-700 flex-shrink-0"><FileText size={14} /></button>
              )}
              {isAdmin && (
                <button onClick={() => remove(r.id)} title="ลบ" className="text-gray-300 hover:text-red-500 flex-shrink-0"><Trash2 size={14} /></button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ── Schedule tab (admin) ─────────────────────────────────────────────────────

interface PeriodSchedule {
  enabled: boolean; time: string; dayOfMonth?: number; month?: number
  hostIds: string[]; allHosts?: boolean; metricsPerHost?: number; provider?: string | null
}

function SchedulePeriodCard({ period, value, hosts, readyProviders, nextRun, onChange, onRunNow }: {
  period: Period; value: PeriodSchedule; hosts: ZbxHost[]; readyProviders: AiProvider[]
  nextRun: string | null; onChange: (v: PeriodSchedule) => void; onRunNow: () => void
}) {
  const [hostsOpen, setHostsOpen] = useState(false)
  const [runningNow, setRunningNow] = useState(false)
  const set = (patch: Partial<PeriodSchedule>) => onChange({ ...value, ...patch })

  return (
    <div className="bg-white border border-gray-200 rounded-2xl p-4 space-y-3">
      <div className="flex items-center gap-2">
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={value.enabled} onChange={e => set({ enabled: e.target.checked })}
            className="w-4 h-4 rounded accent-emerald-600" />
          <span className="text-sm font-semibold text-gray-800">รายงาน{PERIOD_LABEL[period]}</span>
        </label>
        {value.enabled && nextRun && (
          <span className="ml-auto text-xs text-emerald-600 flex items-center gap-1"><Clock size={12} /> รอบถัดไป {fmtDateTime(nextRun)}</span>
        )}
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label className="block text-[11px] text-gray-500 mb-1">เวลา (Asia/Bangkok)</label>
          <input type="time" value={value.time} onChange={e => set({ time: e.target.value })}
            className="border border-gray-200 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
        </div>
        {period === 'monthly' && (
          <div>
            <label className="block text-[11px] text-gray-500 mb-1">วันที่ของเดือน (1–28)</label>
            <input type="number" min={1} max={28} value={value.dayOfMonth ?? 1}
              onChange={e => set({ dayOfMonth: Math.min(28, Math.max(1, Number(e.target.value) || 1)) })}
              className="w-20 border border-gray-200 rounded-lg px-2 py-1.5 text-sm text-center focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
        )}
        {period === 'yearly' && (
          <>
            <div>
              <label className="block text-[11px] text-gray-500 mb-1">เดือน</label>
              <select value={value.month ?? 1} onChange={e => set({ month: Number(e.target.value) })}
                className="border border-gray-200 rounded-lg px-2 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500">
                {Array.from({ length: 12 }, (_, i) => i + 1).map(m => (
                  <option key={m} value={m}>{new Date(2000, m - 1, 1).toLocaleDateString('th-TH', { month: 'long' })}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-[11px] text-gray-500 mb-1">วันที่ (1–28)</label>
              <input type="number" min={1} max={28} value={value.dayOfMonth ?? 1}
                onChange={e => set({ dayOfMonth: Math.min(28, Math.max(1, Number(e.target.value) || 1)) })}
                className="w-20 border border-gray-200 rounded-lg px-2 py-1.5 text-sm text-center focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
          </>
        )}
        <div>
          <label className="block text-[11px] text-gray-500 mb-1">metric/host</label>
          <input type="number" min={1} max={30} value={value.metricsPerHost ?? 12}
            onChange={e => set({ metricsPerHost: Math.min(30, Math.max(1, Number(e.target.value) || 12)) })}
            className="w-16 border border-gray-200 rounded-lg px-2 py-1.5 text-sm text-center focus:outline-none focus:ring-2 focus:ring-blue-500" />
        </div>
        {readyProviders.length > 1 && (
          <div>
            <label className="block text-[11px] text-gray-500 mb-1">AI</label>
            <select value={value.provider ?? ''} onChange={e => set({ provider: e.target.value || null })}
              className="border border-gray-200 rounded-lg px-2 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500">
              <option value="">ค่าเริ่มต้น</option>
              {readyProviders.map(p => <option key={p} value={p}>{PROVIDER_LABEL[p]}</option>)}
            </select>
          </div>
        )}
      </div>

      <label className="flex items-center gap-2 text-sm cursor-pointer">
        <input type="checkbox" checked={!!value.allHosts} onChange={e => set({ allHosts: e.target.checked })}
          className="w-4 h-4 rounded accent-emerald-600" />
        <span className="text-gray-700">ดึงทุก Host ที่เปิดใช้งาน{hosts.length ? ` (${hosts.filter(h => h.enabled).length})` : ''}</span>
      </label>

      {!value.allHosts && (
        <div className="border border-gray-100 rounded-xl overflow-hidden">
          <button onClick={() => setHostsOpen(v => !v)} className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-gray-50 text-sm">
            <Server size={13} className="text-gray-500" />
            <span className="text-gray-700">Host ที่จะดึงข้อมูล</span>
            <span className="text-xs text-gray-400">เลือกแล้ว {value.hostIds.length}</span>
            <ChevronDown size={13} className={`ml-auto text-gray-400 transition-transform ${hostsOpen ? 'rotate-180' : ''}`} />
          </button>
          {hostsOpen && (
            <div className="px-3 pb-3 pt-1 border-t border-gray-100">
              <HostMultiSelect hosts={hosts} selected={value.hostIds} onChange={ids => set({ hostIds: ids })} />
            </div>
          )}
        </div>
      )}

      <div className="flex items-center gap-2">
        <button
          onClick={async () => { setRunningNow(true); try { await onRunNow() } finally { setRunningNow(false) } }}
          disabled={runningNow || (!value.allHosts && !value.hostIds.length)}
          className="flex items-center gap-1.5 text-xs border border-gray-300 hover:bg-gray-50 disabled:opacity-40 text-gray-700 px-3 py-1.5 rounded-lg">
          {runningNow ? <Loader2 size={13} className="animate-spin" /> : <Play size={13} />} สร้างรายงานตอนนี้
        </button>
        {!value.allHosts && !value.hostIds.length && <span className="text-xs text-gray-400">เลือก Host ก่อน</span>}
      </div>
    </div>
  )
}

function ScheduleTab({ hosts, readyProviders }: { hosts: ZbxHost[]; readyProviders: AiProvider[] }) {
  const [sched, setSched] = useState<Record<Period, PeriodSchedule> | null>(null)
  const [nextRuns, setNextRuns] = useState<Record<string, string | null>>({})
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [msg, setMsg] = useState<string | null>(null)

  const load = () => {
    setLoading(true)
    api.get('/zabbix/schedule')
      .then(r => { setSched(r.data.schedule); setNextRuns(r.data.nextRuns || {}) })
      .catch(e => setMsg(e.response?.data?.message || 'โหลดการตั้งเวลาไม่ได้'))
      .finally(() => setLoading(false))
  }
  useEffect(load, [])

  const save = async () => {
    if (!sched) return
    setSaving(true); setMsg(null)
    try {
      const r = await api.put('/zabbix/schedule', sched)
      setSched(r.data.schedule); setNextRuns(r.data.nextRuns || {})
      setMsg('บันทึกเรียบร้อย')
    } catch (e: any) {
      setMsg(e.response?.data?.message || 'บันทึกไม่สำเร็จ')
    } finally { setSaving(false) }
  }
  const runNow = async (period: Period, config: PeriodSchedule) => {
    try {
      const r = await api.post('/zabbix/schedule/run', { period, config })
      alert(r.data.message || 'สร้างรายงานเรียบร้อย — ดูได้ที่แท็บ “ประวัติ”')
    } catch (e: any) {
      alert(e.response?.data?.message || 'สร้างรายงานไม่สำเร็จ')
    }
  }

  if (loading) return <div className="flex justify-center py-10"><Loader2 size={20} className="animate-spin text-gray-300" /></div>
  if (!sched) return <div className="text-sm text-red-600">{msg}</div>

  return (
    <div className="space-y-4">
      <div className="bg-blue-50 border border-blue-100 rounded-xl px-4 py-3 text-xs text-blue-800 flex gap-2">
        <Info size={15} className="flex-shrink-0 mt-0.5" />
        <span>ระบบจะดึงข้อมูล Zabbix และให้ AI สรุปตามรอบที่ตั้งไว้โดยอัตโนมัติ (เวลาอิงเขตเวลา Asia/Bangkok) แล้วบันทึกเข้าแท็บ “ประวัติ” — ดาวน์โหลดเป็น Word/PDF ได้</span>
      </div>

      {(['daily', 'monthly', 'yearly'] as Period[]).map(p => (
        <SchedulePeriodCard key={p} period={p} value={sched[p]} hosts={hosts} readyProviders={readyProviders}
          nextRun={nextRuns[p] ?? null}
          onChange={v => setSched(s => s ? { ...s, [p]: v } : s)}
          onRunNow={() => runNow(p, sched[p])} />
      ))}

      <div className="flex items-center gap-3">
        <button onClick={save} disabled={saving}
          className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-200 text-white text-sm font-medium px-4 py-2.5 rounded-xl">
          {saving ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />} บันทึกการตั้งเวลา
        </button>
        {msg && <span className="text-sm text-gray-500">{msg}</span>}
      </div>
    </div>
  )
}

// ── Main page ────────────────────────────────────────────────────────────────

type TabKey = 'analyze' | 'history' | 'schedule'

export default function ZabbixAnalysisPage({ isAdmin = false }: { isAdmin?: boolean }) {
  const [tab, setTab] = useState<TabKey>('analyze')
  const [hosts, setHosts] = useState<ZbxHost[]>([])
  const [hostsErr, setHostsErr] = useState<string | null>(null)
  const [hostsLoading, setHostsLoading] = useState(true)
  const [readyProviders, setReadyProviders] = useState<AiProvider[]>([])
  const [activeProvider, setActiveProvider] = useState<AiProvider | null>(null)
  const [historyKey, setHistoryKey] = useState(0)

  const loadHosts = () => {
    setHostsLoading(true); setHostsErr(null)
    api.get('/zabbix/hosts')
      .then(r => setHosts(r.data))
      .catch(e => setHostsErr(e.response?.data?.message || 'โหลดรายการ Host ไม่ได้'))
      .finally(() => setHostsLoading(false))
  }
  useEffect(loadHosts, [])
  useEffect(() => {
    api.get('/zabbix/providers')
      .then(r => { setReadyProviders(r.data.ready ?? []); setActiveProvider(r.data.active ?? null) })
      .catch(() => {})
  }, [])

  const TABS: { key: TabKey; label: string; icon: any; adminOnly?: boolean }[] = [
    { key: 'analyze', label: 'วิเคราะห์ใหม่', icon: Activity },
    { key: 'history', label: 'ประวัติ', icon: History },
    { key: 'schedule', label: 'ตั้งเวลา', icon: CalendarClock, adminOnly: true },
  ]

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto space-y-5">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-sm">
          <Activity size={20} className="text-white" />
        </div>
        <div>
          <h1 className="text-lg font-bold text-gray-900">Monitor AI</h1>
          <p className="text-xs text-gray-400">วิเคราะห์สุขภาพและประสิทธิภาพระบบจากข้อมูลมอนิเตอริงด้วย AI — รายวัน / รายเดือน / รายปี</p>
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-100 rounded-xl px-4 py-3 text-xs text-blue-800 flex gap-2">
        <Info size={15} className="flex-shrink-0 mt-0.5" />
        <div className="space-y-0.5">
          <p><strong>แนวทางการดึงข้อมูล:</strong> ใช้ Zabbix API เป็นหลัก (เสถียร ไม่กระทบ DB โดยตรง)</p>
          <p>• <strong>รายวัน</strong> ดึงจาก <code>history</code> · <strong>รายเดือน/รายปี</strong> ดึงจาก <code>trends</code> (ค่าเฉลี่ยรายชั่วโมง)</p>
          <p>• สรุปสถิติต่อ item + เหตุการณ์/ปัญหา แล้วส่งให้ AI เขียนรายงาน — ตั้งเวลาอัตโนมัติและดาวน์โหลดเป็น Word/PDF ได้</p>
        </div>
      </div>

      {isAdmin && <ConfigPanel />}

      <div className="flex items-center gap-1.5 border-b border-gray-200">
        {TABS.filter(t => !t.adminOnly || isAdmin).map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              tab === t.key ? 'border-emerald-600 text-emerald-700' : 'border-transparent text-gray-500 hover:text-gray-800'
            }`}>
            <t.icon size={15} /> {t.label}
          </button>
        ))}
      </div>

      {tab === 'analyze' && (
        <AnalyzeTab isAdmin={isAdmin} hosts={hosts} hostsErr={hostsErr} hostsLoading={hostsLoading}
          reloadHosts={loadHosts} readyProviders={readyProviders} activeProvider={activeProvider}
          onSaved={() => setHistoryKey(k => k + 1)} />
      )}
      {tab === 'history' && <HistoryTab isAdmin={isAdmin} refreshKey={historyKey} />}
      {tab === 'schedule' && isAdmin && <ScheduleTab hosts={hosts} readyProviders={readyProviders} />}
    </div>
  )
}
